import React, { useEffect, useRef } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemText } from '@mui/material';

import { type SearchOption } from '@/types/search';

/**
 * Shared component for rendering options list in autocomplete and dropdown editors
 * Reduces code duplication between AutocompleteEditor and DropdownEditor
 */
interface OptionsListProps {
  options: (SearchOption | string)[];
  selectedValue: string | SearchOption | null;
  onOptionClick: (option: SearchOption | string) => void;
  highlightedIndex?: number;
  maxHeight?: string;
  fontSize?: string;
  paddingY?: number;
  paddingX?: number;
  emptyMessage?: string;
  getOptionKey: (option: SearchOption | string, index: number) => string;
  getOptionLabel: (option: SearchOption | string) => string;
  isOptionSelected: (
    option: SearchOption | string,
    selected: string | SearchOption | null
  ) => boolean;
}

/**
 * OptionsList - Shared component for rendering options in autocomplete/dropdown editors
 */
export const OptionsList: React.FC<OptionsListProps> = ({
  options,
  selectedValue,
  onOptionClick,
  highlightedIndex = -1,
  maxHeight = '300px',
  fontSize = '14px',
  paddingY = 1,
  paddingX = 2,
  emptyMessage = '該当する項目がありません',
  getOptionKey,
  getOptionLabel,
  isOptionSelected,
}) => {
  const optionButtonRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());

  useEffect(() => {
    if (highlightedIndex < 0) return;
    const highlightedOption = optionButtonRefs.current.get(highlightedIndex);
    if (!highlightedOption) return;

    // Keep keyboard-highlighted item visible inside scrollable popup.
    if (typeof highlightedOption.scrollIntoView === 'function') {
      highlightedOption.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const listStyles = {
    padding: 0,
    '& .MuiListItemButton-root:hover': {
      backgroundColor: '#e3f2fd',
    },
    '& .MuiListItemButton-root.Mui-selected': {
      backgroundColor: '#bbdefb',
      '&:hover': {
        backgroundColor: '#90caf9',
      },
    },
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxHeight: maxHeight,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <List dense sx={listStyles}>
        {options.length > 0 ? (
          options.map((option, index) => {
            const optionKey = getOptionKey(option, index);
            const optionLabel = getOptionLabel(option);
            const isSelected = isOptionSelected(option, selectedValue);
            const isHighlighted = index === highlightedIndex;

            return (
              <ListItem
                key={optionKey}
                disablePadding
                sx={{
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <ListItemButton
                  ref={(el) => {
                    optionButtonRefs.current.set(index, el);
                  }}
                  onClick={() => onOptionClick(option)}
                  selected={isSelected || isHighlighted}
                  sx={{
                    py: paddingY,
                    px: paddingX,
                  }}
                >
                  <ListItemText
                    primary={optionLabel}
                    primaryTypographyProps={{
                      style: {
                        fontSize: fontSize,
                        fontWeight: isSelected || isHighlighted ? 600 : 400,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })
        ) : (
          <ListItem>
            <ListItemText
              primary={emptyMessage}
              primaryTypographyProps={{
                style: {
                  fontSize: fontSize,
                  color: '#999',
                  fontStyle: 'italic',
                },
              }}
            />
          </ListItem>
        )}
      </List>
    </Box>
  );
};
